// src/components/calculators/ResultsDisplay.js
import React from 'react';
import { Typography, Card, CardBody } from "@material-tailwind/react";
import { 
  BanknotesIcon,
  ReceiptPercentIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon
} from "@heroicons/react/24/outline";
import { useCalculator } from './CalculatorContext';

const ResultsDisplay = ({ results, mode }) => {
  const { formatCurrency, formatPercentage, CARD_FEE_RATE } = useCalculator();

  if (!results) return null;

  const isQuoteMode = mode === 'quoteBuilder';

  return (
    <Card className="shadow-lg border" style={{ 
      borderColor: 'var(--Stroke-Light)',
      borderRadius: 'var(--radius-m)',
      boxShadow: 'var(--shadow-frame-big)'
    }}>
      <div className="p-4" style={{ 
        background: 'linear-gradient(135deg, var(--Grey-900) 0%, var(--Grey-800) 100%)',
        borderTopLeftRadius: 'var(--radius-m)',
        borderTopRightRadius: 'var(--radius-m)'
      }}>
        <Typography variant="h6" className="font-semibold" style={{
          color: 'var(--Text-Contrast)',
          fontSize: 'var(--font-size-h6)',
          fontWeight: 'var(--font-weight-h6)',
          lineHeight: 'var(--line-height-h6)'
        }}>
          {isQuoteMode ? 'Quote Breakdown' : 'Fee & Earnings Breakdown'}
        </Typography>
      </div>
      
      <CardBody className="space-y-4" style={{ padding: 'var(--card-padding)' }}>
        {/* Selling Price Section */}
        <div className="pb-4" style={{ borderBottom: '1px solid var(--Stroke-Light)' }}>
          {results.vatAmount > 0 && (
            <div className="flex justify-between items-center mb-2">
              <Typography style={{ 
                color: 'var(--Text-Tertiary)',
                fontSize: 'var(--font-size-body4)',
                lineHeight: 'var(--line-height-body4)'
              }}>
                Base Price (Excl. VAT):
              </Typography>
              <Typography style={{ 
                color: 'var(--Text-Tertiary)',
                fontSize: 'var(--font-size-body4)',
                lineHeight: 'var(--line-height-body4)'
              }}>
                {formatCurrency(results.priceBeforeVAT)}
              </Typography>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-5 w-5" style={{ color: 'var(--Blue-700)' }} />
              <Typography className="font-semibold" style={{ 
                color: 'var(--Text-Primary)',
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
                Customer Pays:
              </Typography>
            </div>
            <Typography variant="h5" className="font-bold" style={{ 
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-h5)',
              fontWeight: 'var(--font-weight-h5)',
              lineHeight: 'var(--line-height-h5)'
            }}>
              {formatCurrency(results.sellingPrice)}
            </Typography>
          </div>
        </div>

        {/* Commission Breakdown - Simplified */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ReceiptPercentIcon className="h-5 w-5" style={{ color: 'var(--Blue-700)' }} />
            <Typography className="font-semibold" style={{ 
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              Boutee Commission
            </Typography>
          </div>
          
          {/* Simple Commission List */}
          <div className="pl-7 space-y-1">
            {results.commissionBreakdown.map((tier, index) => (
              <div key={index} className="flex justify-between">
                <Typography style={{ 
                  color: 'var(--Text-Secondary)',
                  fontSize: 'var(--font-size-body4)',
                  lineHeight: 'var(--line-height-body4)'
                }}>
                  Band {index + 1} ({tier.rate}% on {formatCurrency(tier.amount)}):
                </Typography>
                <Typography style={{ 
                  color: 'var(--Text-Secondary)',
                  fontSize: 'var(--font-size-body4)',
                  lineHeight: 'var(--line-height-body4)'
                }}>
                  {formatCurrency(tier.commission)}
                </Typography>
              </div>
            ))}
            
            <div className="flex justify-between items-center pt-2" 
                 style={{ borderTop: '1px solid var(--Stroke-Light)' }}>
              <Typography className="font-semibold" style={{ 
                color: 'var(--Text-Primary)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-semibold)'
              }}>
                Total Commission:
              </Typography>
              <Typography className="font-bold" style={{ 
                color: 'var(--Text-Primary)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
                {formatCurrency(results.totalCommission)}
              </Typography>
            </div>
          </div>
        </div>

        {/* Card Fees */}
        <div className="flex justify-between items-center py-3" 
             style={{ 
               borderTop: '1px solid var(--Stroke-Light)', 
               borderBottom: '1px solid var(--Stroke-Light)',
               paddingTop: 'var(--spacing-s)',
               paddingBottom: 'var(--spacing-s)'
             }}>
          <div className="flex items-center gap-2">
            <CreditCardIcon className="h-5 w-5" style={{ color: 'var(--Blue-700)' }} />
            <Typography className="font-semibold" style={{ 
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              Card Processing ({formatPercentage(CARD_FEE_RATE * 100)}):
            </Typography>
          </div>
          <Typography className="font-semibold" style={{ 
            color: 'var(--Text-Primary)',
            fontSize: 'var(--font-size-body3)',
            fontWeight: 'var(--font-weight-semibold)'
          }}>
            {formatCurrency(results.cardFees)}
          </Typography>
        </div>

        {/* VAT if applicable */}
        {results.vatAmount > 0 && (
          <div className="flex justify-between items-center py-3" 
               style={{ 
                 borderBottom: '1px solid var(--Stroke-Light)',
                 paddingTop: 'var(--spacing-s)',
                 paddingBottom: 'var(--spacing-s)'
               }}>
            <Typography style={{ 
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body4)',
              lineHeight: 'var(--line-height-body4)'
            }}>
              VAT to Remit ({results.vatRateApplied}%):
            </Typography>
            <Typography className="font-semibold" style={{ 
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {formatCurrency(results.vatAmount)}
            </Typography>
          </div>
        )}

        {/* Cost of Goods (for quote mode) */}
        {isQuoteMode && results.costOfGoods > 0 && (
          <div className="flex justify-between items-center py-3" 
               style={{ 
                 borderBottom: '1px solid var(--Stroke-Light)',
                 paddingTop: 'var(--spacing-s)',
                 paddingBottom: 'var(--spacing-s)'
               }}>
            <Typography style={{ 
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body4)',
              lineHeight: 'var(--line-height-body4)'
            }}>
              Your Cost of Goods:
            </Typography>
            <Typography className="font-semibold" style={{ 
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {formatCurrency(results.costOfGoods)}
            </Typography>
          </div>
        )}

        {/* Final Result */}
        <div className="rounded-lg p-4" style={{ 
          background: results.grossProfit >= 0 || results.netAfterVATRemittance >= 0
            ? 'linear-gradient(135deg, rgba(185, 245, 81, 0.1) 0%, rgba(215, 246, 80, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(196, 100, 109, 0.1) 0%, rgba(228, 132, 140, 0.1) 100%)',
          borderRadius: 'var(--radius-m)',
          padding: 'var(--spacing-l)'
        }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Typography className="font-bold" style={{ 
                color: (isQuoteMode ? results.grossProfit : results.netAfterVATRemittance) >= 0 
                  ? 'var(--Green-700)' 
                  : 'var(--Red-700)',
                fontSize: 'var(--font-size-body2)',
                fontWeight: 'var(--font-weight-bold)',
                lineHeight: 'var(--line-height-body2)'
              }}>
                {isQuoteMode ? 'Your Profit:' : 'You Receive:'}
              </Typography>
            </div>
            <Typography 
              variant="h4" 
              className="font-bold"
              style={{
                color: (isQuoteMode ? results.grossProfit : results.netAfterVATRemittance) < 0 
                  ? 'var(--Red-700)' 
                  : 'var(--Green-700)',
                fontSize: 'var(--font-size-h4)',
                fontWeight: 'var(--font-weight-h4)',
                lineHeight: 'var(--line-height-h4)'
              }}
            >
              {formatCurrency(
                isQuoteMode ? results.grossProfit : results.netAfterVATRemittance
              )}
            </Typography>
          </div>
          
          <Typography variant="small" className="mt-2" style={{ 
            color: 'var(--Grey-600)',
            fontSize: 'var(--font-size-body5)',
            lineHeight: 'var(--line-height-body5)',
            marginTop: 'var(--spacing-m)',
            textAlign: 'center',
          }}>
            {isQuoteMode 
              ? 'After all costs, fees, and VAT remittance'
              : 'After Boutee commission, card fees, and VAT remittance'
            }
          </Typography>
        </div>
      </CardBody>
    </Card>
  );
};

export default ResultsDisplay;